/* eslint-disable no-case-declarations */
/* eslint-disable jsx-a11y/label-has-for */
import { useState, useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import gsap from 'gsap'
import usePrevious from '@/hooks/usePrevious'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import style from './style'

const useStyles = createUseStyles(style)

const Form = ({
  data,
  privacy,
  privacyLink,
  newsletter,
  newsletterLink,
  labelSubmit,
  sendForm,
  resetForm,
  response,
}) => {
  const classes = useStyles(style)
  const [value, setValue] = useState({})
  const [message, setMessage] = useState()
  const [consentSubmit, setConsentSubmit] = useState(false)
  const prevResponse = usePrevious(response)
  const $form = useRef(null)
  const $message = useRef(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [token, setToken] = useState('')

  /*------------------------------
  Update Response
  ------------------------------*/
  const updateResponse = () => {
    setMessage(response.message)
    setValue({})
    resetForm()

    // Animazione messaggio
    gsap.fromTo($message.current, {
      opacity: 0,
    }, {
      duration: 0.4,
      opacity: 1,
      onComplete: () => {
        // Dopo 5 secondi lo nascondo
        gsap.to($message.current, {
          duration: 0.5,
          opacity: 0,
          delay: 5,
        })

        // Resetto form
        $form.current.querySelectorAll('input').forEach((i) => i.value = '')
        $form.current.querySelectorAll('textarea').forEach((i) => i.value = '')
        $form.current.querySelectorAll('input[type="checkbox"]').forEach((i) => i.checked = false)
      },
    })
  }

  /*------------------------------
  Response Change
  ------------------------------*/
  useUpdateEffect(() => {
    if (prevResponse !== response) {
      if (response.message !== '') {
        updateResponse()
      }
    }
  }, [response])

  /*------------------------------
  Handle Submit
  ------------------------------*/
  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!executeRecaptcha) return

    const result = await executeRecaptcha('contact_page')
    setToken(result)
  }

  /*------------------------------
  Handle Change
  ------------------------------*/
  const handleChange = (event) => {
    event.target.classList.remove('focus-visible')
    const newValue = { ...value }
    newValue[event.target.id] = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    setValue(newValue)
  }

  useEffect(() => {
    if (token) {
      setValue((prevState) => ({
        ...prevState,
        'g-recaptcha-response': token,
        '_wpcf7_recaptcha_response': token, // eslint-disable-line
      }))
      setConsentSubmit(true)
    }
  }, [token])

  useEffect(() => {
    if (consentSubmit) {
      sendForm(value)
      setTimeout(() => {
        setConsentSubmit(false)
      }, 1000)
    }
  }, [consentSubmit])

  /*------------------------------
  Get Field
  ------------------------------*/
  const getField = (item, index) => {
    const { name, type, placeholder, label } = item
    const required = item.required ? 'required' : ''

    switch (type) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
      case 'number':
      case 'date':
        return (
          <div key={index.toString()} className={classes.formControl}>
            {/* {label && (<label htmlFor={name} className={classes.label}>{label}</label>)} */}
            <input
              onChange={handleChange}
              id={name}
              className={`${classes.input} ${classes[type]}`}
              required={required}
              placeholder={placeholder}
              onFocus={(e) => e.target.classList.remove('focus-visible')}
            />
          </div>
        )
      // Select
      case 'select':
        const firstOption = item.include_blank ? (<option value="" disabled selected>{item.label}</option>) : null
        const options = item.options.map((val) => {
          return (<option key={val} value={val}>{val}</option>)
        })
        return (
          <div key={index.toString()} className={classes.formControl}>
            <select onChange={handleChange} id={name} required={required} className={classes.select}>
              {firstOption}
              {options}
            </select>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 59 82" className={classes.selectArrow}>
              <path d="M29.5,0l29.5,28l-59,0l29.5,-28Z" />
              <path d="M29.5,82l29.5,-28l-59,0l29.5,28Z" />
            </svg>
          </div>
        )
        // Textarea
      case 'textarea':
        return (
          <div key={index.toString()} className={`${classes.formControl} ${classes.formControlTextarea}`}>
            <textarea
              onChange={handleChange}
              id={name}
              placeholder={item.placeholder}
              required={required}
              className={classes.textarea}
              rows={3}
              onFocus={(e) => e.target.classList.remove('focus-visible')}
            />
          </div>
        )
      case 'checkbox':
        return (
          <div key={index.toString()} className={`${classes.formControl} ${classes.formControlCheckbox}`}>
            {name === 'privacy' && (
            <label htmlFor={name} className={classes.checkbox}>
              <input id={name} type="checkbox" onChange={handleChange} required={required} />
              <span><a href={privacyLink} target="_blank" rel="noopener">{privacy}</a></span>
            </label>
            )}
            {name === 'usemailup' && (
            <label htmlFor={name} className={classes.checkbox}>
              <input id={name} type="checkbox" onChange={handleChange} required={required} />
              <span><a href={newsletterLink} target="_blank" rel="noopener">{newsletter}</a></span>
            </label>
            )}
          </div>
        )
      default:
        return null
    }
  }

  /*------------------------------
  Render Data
  ------------------------------*/
  const renderData = () => data.map((i, ind) => getField(i, ind))

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit} autoComplete="off" ref={$form}>
        {renderData()}
        <div className={`${classes.formControl} ${classes.formControlSubmit}`}>
          <button
            type="submit"
            className={classes.submit}
            aria-label="submit-form"
          >
            <span>{labelSubmit}</span>
          </button>
        </div>
        <div className={classes.message} ref={$message}>{message}</div>
      </form>
    </div>
  )
}

export default Form
